import type { LoaderFunctionArgs } from '@remix-run/node';
import { json } from '@remix-run/node';
import { findPlaceByKeyword } from '~/modules/location/api/find-place-by-keyword.server';

// doc: https://archipro.atlassian.net/wiki/spaces/AD/pages/1777139713/Place+Finder
// gql: packages/web/app/modules/location/graphql/query/find-place.graphql

export const FIND_PLACE_REMIX_API = '/remix-api/location/find-place-by-keyword';

/**
 * Place results contain no user specific data and only vary by the query
 * string (keyword, type, limit, country, apiProvider), so successful
 * responses are publicly cacheable. This lets the CDN absorb hot prefixes
 * (e.g. "auck").
 */
const PLACE_RESULTS_CACHE_CONTROL = 'public, max-age=3600';
const PLACE_RESULTS_ERROR_CACHE_CONTROL = 'no-store';

/** Attach no-store so CDN edges never cache transient backend failures. */
const withNoStoreCacheControl = (response: Response): Response => {
    const headers = new Headers(response.headers);
    headers.set('Cache-Control', PLACE_RESULTS_ERROR_CACHE_CONTROL);
    return new Response(response.body, {
        status: response.status,
        statusText: response.statusText,
        headers,
    });
};

export const loader = async (args: LoaderFunctionArgs) => {
    try {
        const places = await findPlaceByKeyword(args);

        return json(places, {
            headers: { 'Cache-Control': PLACE_RESULTS_CACHE_CONTROL },
        });
    } catch (error) {
        args.context.logger.error('find-place-by-keyword failed', error);

        if (error instanceof Response) {
            throw withNoStoreCacheControl(error);
        }

        throw json([], {
            status: 503,
            headers: { 'Cache-Control': PLACE_RESULTS_ERROR_CACHE_CONTROL },
        });
    }
};
